import React, { useEffect, useState, useMemo } from 'react';
import {
  Checkbox,
  Select,
  Divider,
  Switch,
  Input,
  Form,
  DatePicker,
  Radio
} from 'antd';
import { FieldNumberOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import IBAN from 'iban';
import PropTypes from 'prop-types';
import moment from 'moment';
import useXmlPattern from '../../utils/xmlPattern';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { isValidZipcode } from '../../utils/zipcode';
import QuittanceTable from '../contracts/QuittanceTable';

const StepModalTwo = ({
  formModalValues,
  quote,
  generateQuittances,
  quittances,
  setFormModalValues
}) => {
  const { t } = useTranslation();
  const xmlPattern = useXmlPattern();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [enums, setEnums] = useState({});
  const [bics, setBics] = useState([]);
  const [cities, setCities] = useState();
  const [cityLoading, setCityLoading] = useState(false);
  const dateFormat = 'DD/MM/YYYY';

  const rootForm = Form.useFormInstance();

  // const fieldsValidationStatus = useMemo(() => {
  //   const fieldMap = {
  //     bank_postal_code: null
  //   };

  //   if (rootForm.isFieldTouched('bank_postal_code')) {
  //     const { bank_postal_code } = rootForm.getFieldsValue();
  //     if (!isValidZipcode(bank_postal_code)) {
  //       fieldMap.bank_postal_code = 'error';
  //     } else if (cityLoading) {
  //       fieldMap.bank_postal_code = 'validating';
  //     } else if (!cities?.length) {
  //       fieldMap.bank_postal_code = 'error';
  //     } else {
  //       fieldMap.bank_postal_code = 'success';
  //     }
  //   }

  //   return fieldMap;
  // }, [rootForm, cityLoading, cities]);

  const getTaxAmount = ({ type }) => {
    if (type === 'MRP' || type === 'MRI') return 6.5;
    return 0;
  };

  const needToReportFirstQuittanceToNextMonth =
    formModalValues?.effective_date &&
    moment(formModalValues.effective_date).format('LL') ===
      moment().startOf('month').format('LL') &&
    moment().format('LL') === moment().startOf('month').format('LL');

  const { Option } = Select;

  const formMentionLayout = {
    wrapperCol: {
      span: 40,
      offset: 0
    }
  };

  const firstQuittanceToBePaid =
    quittances?.length > 0
      ? quittances.filter((q) => q.quittance_status === 'NOT_PAID')[0]
      : undefined;

  const arrayQuittanceToBePaid =
    quittances?.length > 0
      ? quittances.filter((q) => q.quittance_status === 'NOT_PAID').slice(0, 1)
      : undefined;

  const paidQuittance = quote?.quittances.find(
    (q) => q.quittance_status === 'PAID'
  );

  const isDisabled = paidQuittance;

  const startDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[0] : ''
  ).format('DD/MM/YYYY');

  const startDebitDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.period_date : ''
  )
    .startOf('month')
    .startOf('day')
    .utc(true)
    .format('DD/MM/YYYY');

  const endRangeZeroDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[0] : ''
  )
    .endOf('month')
    .format('DD/MM/YYYY');

  const endRangeTwoDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[1] : ''
  )
    .endOf('month')
    .add(1, 'month')
    .format('DD/MM/YYYY');

  const startRangeTwoDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[1] : ''
  )
    .startOf('month')
    .add(1, 'month')
    .format('DD/MM/YYYY');

  const endDebitDate = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[1] : ''
  )
    .utc(true)
    .format('DD/MM/YYYY');

  const firstDebit = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.period_date : ''
  )
    .startOf('month')
    .add(5, 'day')
    .utc(true)
    .format('DD/MM/YYYY');

  const rangeTwoDebit = moment(
    firstQuittanceToBePaid ? firstQuittanceToBePaid.range_concerned[1] : ''
  )
    .add(6, 'day')
    .format('DD/MM/YYYY');

  const secondDebit = moment(
    quittances && quittances[1] && quittances[1].period_date
  )
    .startOf('month')
    .add(5, 'day')
    .utc(true)
    .format('DD/MM/YYYY');

  const isARealProrata = () => {
    const { fraction_type } = quote;
    const startDate2 = moment(formModalValues?.effective_date);
    const endOfYear = moment().endOf('year');
    const diff = endOfYear.diff(startDate2, 'days');
    if (!formModalValues?.effective_date) {
      return false;
    }
    if (
      fraction_type === 'MONTHLY' &&
      new Date(startDate2).getMonth() === 11 &&
      diff >= 31
    ) {
      return true;
    }
    if (
      fraction_type === 'QUARTERLY' &&
      new Date(startDate2).getMonth() >= 9 &&
      diff >= 92
    ) {
      return true;
    }
    if (
      fraction_type === 'BIANNUAL' &&
      new Date(startDate2).getMonth() >= 5 &&
      diff >= 184
    ) {
      return true;
    }
    if (
      fraction_type === 'YEARLY' &&
      moment(formModalValues?.effective_date).format('DD/MM/YYYY') ===
        moment().startOf('year').format('DD/MM/YYYY')
    ) {
      return true;
    }
    return false;
  };

  const getCity = async (zipCode) => {
    setCityLoading(true);
    try {
      if (!isValidZipcode(zipCode)) {
        return [];
      }

      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/zipcode/${zipCode}`
      });

      const cities = data.map((city) => ({
        ...city,
        label: `${city.name} (${city.zipcode})`
      }));

      setCityLoading(false);
      return cities;
    } catch (e) {
      setCityLoading(false);
      if (e.response) message(e.response.status);
      return [];
    }
  };

  const handlePostalCodeChange = async (event) => {
    const { value } = event.target;

    const cities = await getCity(value);
    setCities(cities);

    if (!cities?.length > 0) {
      if (rootForm.getFieldValue('bank_city')) {
        rootForm.setFieldValue('bank_city', '');
      }
      return;
    }

    const matchedCity = cities.find(
      (city) => city.name?.toLowerCase() === value?.toLowerCase()
    );

    const newSelectedCity = matchedCity || cities[0];

    rootForm.setFieldValue('bank_city', newSelectedCity.name);
  };

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/contracts/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getBics = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: 'bics' });
      setBics(data.map((b) => b.code));
    } catch (e) {
      setBics([]);
      message(e);
    }
  };

  useEffect(() => {
    document.getElementById('top').scrollIntoView({ behavior: 'smooth' });
    (async () => {
      await getEnums();
      await getBics();
      if (quote?.payment_type !== 'DIRECTDEBIT')
        setFormModalValues({ payment_type: 'CARD' });
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div id="top">
      <p style={{ marginTop: 20 }}>
        {t(
          `validate_quote.modal.transform_${
            quote?.previous_contract ? 'amendment' : 'quote'
          }`
        )}
      </p>
      <Divider orientation="left">Date d&#39;effet</Divider>
      <Form.Item
        name={['effective_date']}
        label={t('validate_quote.form.effective_date_details')}
        rules={[{ required: true }]}
        initialValue={
          paidQuittance ? moment(paidQuittance.range_concerned[0]) : undefined
        }
        tooltip={
          quote.type === 'ASSOCIATION'
            ? t(
                `quotes.tooltip.${
                  quote?.previous_contract ? 'amendment_asso' : 'contract_asso'
                }`
              )
            : t(
                `quotes.tooltip.${
                  quote?.previous_contract ? 'amendment' : 'contract'
                }`
              )
        }
      >
        <DatePicker
          format={dateFormat}
          style={{ width: 200 }}
          onChange={(date) => {
            generateQuittances({
              effective_date: moment(date.toDate('YYYY-MM-DD'))
            });
          }}
          disabled={isDisabled}
          defaultValue={
            paidQuittance ? moment(paidQuittance.range_concerned[0]) : undefined
          }
          disabledDate={(current) => {
            let before = current < moment().startOf('day');
            let after;
            if (quote?.previous_contract) {
              after = current >= moment().add(31, 'day');
            } else {
              after = current >= moment().add(1, 'year');
            }
            return before || after;
          }}
        />
      </Form.Item>
      {quittances && quittances.length ? (
        <ul>
          {(firstQuittanceToBePaid?.prorata &&
            quittances.length !== 1 &&
            quote?.fraction_type === 'MONTHLY') ||
          needToReportFirstQuittanceToNextMonth ? (
            <li>
              {`La cotisation pour la période du ${startDate} au ${
                needToReportFirstQuittanceToNextMonth
                  ? endRangeZeroDate
                  : moment(
                      quittances &&
                        firstQuittanceToBePaid &&
                        firstQuittanceToBePaid.period_date
                        ? firstQuittanceToBePaid.period_date
                        : ''
                    )
                      .subtract(1, 'month')
                      .endOf('month')
                      .format('DD/MM/YYYY')
              } est de  ${
                needToReportFirstQuittanceToNextMonth
                  ? firstQuittanceToBePaid?.selling_price_customer?.toFixed(2)
                  : firstQuittanceToBePaid?.prorata?.toFixed(2)
              } euros.`}
            </li>
          ) : (
            ''
          )}
          {(firstQuittanceToBePaid?.prorata &&
            quittances.length !== 1 &&
            quote?.fraction_type === 'MONTHLY') ||
          needToReportFirstQuittanceToNextMonth ? (
            <li>
              {` ${
                quote?.type === 'RCNGAWK'
                  ? 'Elle doit être réglée avant transformation du devis'
                  : `Elle sera ${
                      firstQuittanceToBePaid.payment_choice === 'CARD'
                        ? 'due'
                        : 'prélevée'
                    } le ${
                      needToReportFirstQuittanceToNextMonth
                        ? rangeTwoDebit
                        : firstDebit
                    }`
              } en même temps que la cotisation de ${
                needToReportFirstQuittanceToNextMonth
                  ? firstQuittanceToBePaid?.selling_price_customer?.toFixed(2) -
                    (['MRI', 'MRP', 'ASSOCIATION'].includes(quote.type) &&
                    !quote.previous_contract
                      ? getTaxAmount(quote)
                      : 0)
                  : (
                      firstQuittanceToBePaid?.selling_price_customer -
                      firstQuittanceToBePaid?.prorata -
                      (['MRI', 'MRP', 'ASSOCIATION'].includes(quote.type) &&
                      !quote.previous_contract
                        ? getTaxAmount(quote)
                        : 0)
                    )?.toFixed(2)
              } euros pour la période du ${
                needToReportFirstQuittanceToNextMonth
                  ? startRangeTwoDate
                  : startDebitDate
              } au ${
                needToReportFirstQuittanceToNextMonth
                  ? endRangeTwoDate
                  : endDebitDate
              }, pour un total de 
              ${(
                firstQuittanceToBePaid.selling_price_customer *
                (needToReportFirstQuittanceToNextMonth ? 2 : 1)
              ).toFixed(2)} euros.`}
            </li>
          ) : (
            <li>
              {`${
                quote?.type === 'RCNGAWK'
                  ? 'La cotisation doit être réglée avant transformation du devis'
                  : `La cotisation sera ${
                      firstQuittanceToBePaid?.payment_choice === 'CARD'
                        ? 'due'
                        : 'prélevée'
                    } à la date du ${firstDebit} `
              } pour la période du ${startDate}
              au ${endDebitDate}, pour la somme totale de 
              ${
                isARealProrata()
                  ? firstQuittanceToBePaid?.prorata?.toFixed(2)
                  : (firstQuittanceToBePaid?.selling_price_customer).toFixed(2)
              }
               euros.`}
            </li>
          )}
          {quote?.fraction_type !== 'MONTHLY' && quittances.length > 1 && (
            <li>
              {`La cotisation suivante sera ${
                quittances[1]?.payment_choice === 'CARD'
                  ? 'due au maximum'
                  : 'prélevée'
              } à la date du ${secondDebit} pour la période du ${moment(
                quittances[1]?.range_concerned[0]
              )
                .startOf('day')
                .utc(true)
                .format('DD/MM/YYYY')} au ${moment(
                quittances[1]?.range_concerned[1]
              )
                .startOf('day')
                .utc(true)
                .format(
                  'DD/MM/YYYY'
                )} pour la somme de ${quittances[1]?.selling_price_customer.toFixed(
                2
              )} euros.`}
            </li>
          )}
          {quittances
            ?.filter((q) => q.quittance_status !== 'PAID')
            .some((qItem) => qItem.attack_tax) ? (
            // eslint-disable-next-line react/jsx-indent
            <li>
              Taxe attentat de 6.50€ en sus à la souscription puis dans
              l&apos;échéance de janvier de chaque année.
            </li>
          ) : (
            ''
          )}
          {quote.paperwork_fee ? (
            <li>
              {`Des frais de dossier en sus de la souscription de ${quote.paperwork_fee.toFixed(
                2
              )} euros seront ${
                quote.paperwork_fee_payment === 'CB' ||
                quote.paperwork_fee_payment === 'FREE'
                  ? 'dus'
                  : 'prélevés'
              } ${
                quote.paperwork_fee_payment === 'FREE'
                  ? 'par tout moyen à la convenance du courtier'
                  : 'à la souscription du contrat'
              }.`}
            </li>
          ) : (
            ''
          )}
          {quote.type === 'RCNGAWK' && quote.fraction_type === 'MONTHLY' ? (
            <li>
              {`Un dépot de garantie d'un mois correspondant à ${(quote.deposit_amount ?? quote.total_monthly_customer).toFixed(
                2
              )} euros est inclus dans la première quittance. Le dépôt de garantie est exigé en cas de fractionnement mensuel, il sera restitué à la fin du contrat pour un contrat ayant été en cours  au minium 12 mois et sans aucune mensualité rejetée par la banque du client. Si au moins une des conditions n’est pas respectée celui-ci sera conservé par Assurmax au titre des pénalités. En aucun cas ce dépôt de garantie ne pourra servir à compenser un éventuel rejet de prélèvement.`}
            </li>
          ) : (
            ''
          )}
          {quote.rootcontract ? (
            <li>
              Des remboursements ou régularisations seront appliqués
              automatiquement si la cotisation de cet avenant est plus ou moins
              elevée que le contrat précédent ou si des factures ont été réglées
              au préalable.
            </li>
          ) : (
            []
          )}
        </ul>
      ) : (
        []
      )}
      {quote?.type === 'RCNGAWK' &&
      (firstQuittanceToBePaid || paidQuittance) ? (
        <QuittanceTable
          data={paidQuittance ? [paidQuittance] : arrayQuittanceToBePaid}
          contractStatus={quote?.status}
          lazy
        />
      ) : (
        ''
      )}

      {quote.payment_type === 'DIRECTDEBIT' ? (
        <>
          <Divider orientation="left">Informations bancaires</Divider>
          <Form.Item
            name={['iban']}
            label={t('validate_quote.form.iban')}
            hasFeedback={IBAN}
            rules={[
              { required: true },
              () => ({
                validator(_, value) {
                  if (IBAN.isValid(value)) return Promise.resolve();
                  // eslint-disable-next-line prefer-promise-reject-errors
                  return Promise.reject('IBAN invalide');
                }
              })
            ]}
          >
            <Input
              placeholder={t('validate_quote.form.iban_placeholder')}
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
          <Form.Item
            name={['bic']}
            label={t('validate_quote.form.bic')}
            rules={[{ required: true }]}
            hasFeedback={formModalValues && formModalValues.bic}
          >
            <Select
              showSearch
              style={{ width: '100%', textAlign: 'left', borderRadius: 5 }}
              placeholder={
                <>
                  <FieldNumberOutlined
                    style={{ color: 'var(--textColorSecondary)' }}
                  />
                  &nbsp;
                  {t('insurers.form.bic_simple')}
                </>
              }
            >
              {bics.map((g) => (
                <Option value={g} key={g}>
                  {t(`${g}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={['bank_name']}
            label={t('validate_quote.form.bank_name')}
            rules={[
              { required: !['RCPML', 'MRPML', 'RCGLS'].includes(quote?.type) }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['bank_address']}
            label={t('validate_quote.form.bank_address')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['bank_postal_code']}
            label={t('validate_quote.form.bank_postal_code')}
            hasFeedback
            // validateStatus={fieldsValidationStatus.bank_postal_code}
            // status={fieldsValidationStatus.bank_postal_code}
            onChange={handlePostalCodeChange}
          >
            <Input style={{ width: '90px' }} autoComplete="off" minLength={5} />
          </Form.Item>
          <Form.Item
            name={['bank_city']}
            label={t('validate_quote.form.bank_city')}
          >
            <Select
              showSearch
              style={{ width: '250px' }}
              options={cities}
              fieldNames={{ value: 'name' }}
            />
          </Form.Item>

          <Divider orientation="left">
            Le RIB est-il celui du souscripteur?
          </Divider>
          <Form.Item name={['rib_not_subscriber']} valuePropName="checked">
            <Switch
              checkedChildren={t('quotes.form.switch.no')}
              unCheckedChildren={t('quotes.form.switch.yes')}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue('rib_not_subscriber') ? (
                <>
                  <p>{t('validate_quote.form.rib_not_subscriber')}</p>
                  <Form.Item name={['rib_type']}>
                    <Radio.Group
                      style={{
                        flexFlow: 'row wrap',
                        marginBottom: 10,
                        marginTop: 10
                      }}
                    >
                      <Radio value="INDIVIDUAL">
                        {t('validate_quote.form.radio_individual')}
                      </Radio>
                      <Radio value="COMPANY">
                        {t(
                          `validate_quote.form.radio_${
                            quote.type === 'ASSOCIATION' ? 'asso' : 'company'
                          }`
                        )}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {getFieldValue('rib_type') === 'COMPANY' ? (
                    <Form.Item
                      name={['rib_company_name']}
                      label={t(
                        `validate_quote.form.rib_${
                          quote.type === 'ASSOCIATION' ? 'asso' : 'company'
                        }_name`
                      )}
                      rules={[{ required: true }, ...xmlPattern]}
                    >
                      <Input />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item
                        name={['rib_civility']}
                        label={t('validate_quote.form.rib_civility')}
                        rules={[{ required: true }]}
                      >
                        <Select>
                          {enums?.genders?.map((item) => (
                            <Option key={item} value={item}>
                              {t(`validate_quote.form.${item}`)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name={['rib_first_name']}
                        label={t('validate_quote.form.rib_first_name')}
                        rules={[{ required: true }, ...xmlPattern]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name={['rib_last_name']}
                        label={t('validate_quote.form.rib_last_name')}
                        rules={[{ required: true }, ...xmlPattern]}
                      >
                        <Input />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item
                    name={['rib_address']}
                    label={t('validate_quote.form.rib_address')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_address_details']}
                    label={t('validate_quote.form.rib_address_details')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_postal_code']}
                    label={t('validate_quote.form.rib_postal_code')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['rib_city']}
                    label={t('validate_quote.form.rib_city')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>
          {quote?.type === 'RCGLS' && (
            <Form.Item
              {...formMentionLayout}
              name={['subscriber_iban_confirmation']}
              valuePropName="checked"
              rules={[{ required: true }]}
            >
              <Checkbox>
                {t('validate_quote.form.subscriber_iban_confirmation')}
              </Checkbox>
            </Form.Item>
          )}
        </>
      ) : (
        []
      )}
      {quote.payment_type !== 'DIRECTDEBIT' ? (
        <>
          <Divider orientation="left">Type de paiement</Divider>
          <Form.Item name={['payment_type']} rules={[{ required: true }]}>
            <Radio.Group
              style={{
                flexFlow: 'row wrap',
                marginBottom: 10,
                marginTop: 10
              }}
              defaultValue="CARD"
            >
              <Radio value="CARD">
                {t('validate_quote.payment_type.CARD')}
              </Radio>
              <Radio value="TRANSFER">
                {t('validate_quote.payment_type.TRANSFER')}
              </Radio>
            </Radio.Group>
          </Form.Item>
          <p>
            {t(
              `validate_quote.payment_type.${
                formModalValues?.payment_type && formModalValues?.payment_type
              }_details`
            )}
          </p>
        </>
      ) : (
        []
      )}
      <Divider />
      <Form.Item
        {...formMentionLayout}
        name={['email_authorization']}
        valuePropName="checked"
        rules={[{ required: true }]}
      >
        <Checkbox>{t('validate_quote.form.email_authorization')}</Checkbox>
      </Form.Item>
      <p>{t('validate_quote.form.gdpr_info')}</p>
      <Form.Item
        {...formMentionLayout}
        name={['gdpr_consent']}
        valuePropName="checked"
        rules={[{ required: true }]}
      >
        <Checkbox>{t('validate_quote.form.gdpr_consent')}</Checkbox>
      </Form.Item>
    </div>
  );
};

StepModalTwo.propTypes = {
  setFormModalValues: PropTypes.func.isRequired,
  quote: PropTypes.shape({
    paperwork_fee_payment: PropTypes.string,
    paperwork_fee: PropTypes.number,
    payment_type: PropTypes.string,
    total_monthly_customer: PropTypes.number,
    previous_contract: PropTypes.string,
    type: PropTypes.string,
    effective_date: PropTypes.string,
    fraction_type: PropTypes.string,
    rootcontract: PropTypes.string,
    status: PropTypes.string,
    malj_member: PropTypes.bool,
    local: PropTypes.bool,
    quittances: PropTypes.array
  }).isRequired,
  formModalValues: PropTypes.shape({
    payment_type: PropTypes.string,
    effective_date: PropTypes.string,
    bic: PropTypes.string
  }),
  generateQuittances: PropTypes.func.isRequired,
  quittances: PropTypes.arrayOf(
    PropTypes.shape({
      range_concerned: PropTypes.arrayOf(PropTypes.string),
      period_date: PropTypes.string,
      prorata: PropTypes.bool,
      payment_choice: PropTypes.string,
      selling_price_customer: PropTypes.number
    })
  )
};

StepModalTwo.defaultProps = {
  formModalValues: null,
  quittances: undefined
};

export default StepModalTwo;
